/**
 * 번호 검사 (기반 공사 5단계 공통 도구, 공통/데이터원칙 0-7)
 *
 * 화면과 서버가 이 한 곳을 같이 쓴다. 외부 연동 없이 일손이 안에서 하는 검사만 둔다.
 * - 주민등록번호, 외국인등록번호: 형식(13자리, 실제 있는 날짜, 성별 자리)이 틀리면 저장할 수 없다.
 *   마지막 자리 계산(검증번호)은 하지 않는다. 2020년 10월 뒤에 받은 번호는 진짜도 계산이 안 맞을 수 있다(2026-11-02 대표님).
 *   자료 옮기기로 옮겨 오는 자료는 막지 않고 번호 칸만 비운다(2026-11-04). 그 판단은 옮기는 쪽이 이 결과를 보고 한다.
 * - 사업자등록번호: 10자리 계산식이 틀리면 저장할 수 없다(2026-11-04 대표님, 계속 막는 것 다섯).
 * - 나이: 만 15세 미만은 등록을 막고, 만 18세 미만은 배차 전에 경고한다.
 * - 휴대전화, 계좌번호: 모양이 이상하면 알리기만 한다.
 */

export type IdCheck =
  | { ok: true; digits: string; birth: string; sex: '남' | '여'; foreign: boolean }
  | { ok: false; reason: '자릿수' | '날짜' | '성별 자리' };

/** 숫자만 남긴다(하이픈, 띄어쓰기를 지움). */
export function onlyDigits(value: string): string {
  return value.replace(/[^0-9]/g, '');
}

function validDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1) return false;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return d <= last;
}

/**
 * 주민등록번호와 외국인등록번호의 형식을 본다. 맞으면 생년월일(YYYY-MM-DD), 성별, 외국인 여부를 돌려준다.
 * 7번째 자리: 1, 2 = 1900년대 내국인, 3, 4 = 2000년대 내국인, 5, 6 = 1900년대 외국인, 7, 8 = 2000년대 외국인.
 * 홀수는 남, 짝수는 여. 오늘보다 뒤에 태어난 날짜는 틀린 날짜로 본다.
 */
export function checkResidentNo(value: string, today: string = todayKst()): IdCheck {
  const digits = onlyDigits(value);
  if (digits.length !== 13) return { ok: false, reason: '자릿수' };
  const g = Number(digits[6]);
  if (g < 1 || g > 8) return { ok: false, reason: '성별 자리' };
  const century = g === 1 || g === 2 || g === 5 || g === 6 ? 1900 : 2000;
  const y = century + Number(digits.slice(0, 2));
  const m = Number(digits.slice(2, 4));
  const d = Number(digits.slice(4, 6));
  if (!validDate(y, m, d)) return { ok: false, reason: '날짜' };
  const birth = `${y}-${pad(m)}-${pad(d)}`;
  if (birth > today) return { ok: false, reason: '날짜' };
  return { ok: true, digits, birth, sex: g % 2 === 1 ? '남' : '여', foreign: g >= 5 };
}

/** 화면에 보여 줄 말. 저장할 수 없다는 뜻을 담는다. */
export function residentNoMessage(r: IdCheck): string | null {
  if (r.ok) return null;
  if (r.reason === '자릿수') return '주민등록번호(외국인등록번호) 13자리를 모두 넣어 주세요.';
  if (r.reason === '날짜') return '앞 6자리 생년월일이 맞지 않아요. 번호를 다시 확인해 주세요.';
  return '뒷자리 첫 숫자가 맞지 않아요. 번호를 다시 확인해 주세요.';
}

/** 사업자등록번호 마지막 자리(검증번호)를 앞 9자리로 계산한다. */
export function bizNoCheckDigit(first9: string): number {
  const w = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(first9[i]) * (w[i] ?? 0);
  sum += Math.floor((Number(first9[8]) * 5) / 10);
  return (10 - (sum % 10)) % 10;
}

export type BizCheck = { ok: true; digits: string } | { ok: false; reason: '자릿수' | '계산' };

/** 사업자등록번호 10자리와 계산식을 본다. 틀리면 저장할 수 없다. */
export function checkBizNo(value: string): BizCheck {
  const digits = onlyDigits(value);
  if (digits.length !== 10) return { ok: false, reason: '자릿수' };
  if (bizNoCheckDigit(digits.slice(0, 9)) !== Number(digits[9])) return { ok: false, reason: '계산' };
  return { ok: true, digits };
}

export function bizNoMessage(r: BizCheck): string | null {
  if (r.ok) return null;
  return r.reason === '자릿수' ? '사업자번호 10자리를 넣어 주세요.' : '없는 사업자번호예요. 번호를 다시 확인해 주세요.';
}

/** 만 나이. birth, on은 YYYY-MM-DD. */
export function ageOn(birth: string, on: string): number {
  const [by, bm, bd] = birth.split('-').map(Number) as [number, number, number];
  const [y, m, d] = on.split('-').map(Number) as [number, number, number];
  let age = y - by;
  if (m < bm || (m === bm && d < bd)) age -= 1;
  return age;
}

/** 만 15세 미만은 등록을 막고, 만 18세 미만은 배차 전에 경고한다(근로기준법 제64조, 제65조). */
export function ageRule(birth: string, on: string = todayKst()): '막음' | '경고' | null {
  const age = ageOn(birth, on);
  if (age < 15) return '막음';
  if (age < 18) return '경고';
  return null;
}

/** 휴대전화: 010으로 시작하는 11자리가 아니면 알린다(막지 않음). */
export function phoneLooksOdd(value: string): boolean {
  return !/^010[0-9]{8}$/.test(onlyDigits(value));
}

/**
 * 계좌번호: 은행마다 자릿수가 달라 10~16자리 숫자가 아니면 알린다(막지 않음).
 * 은행별 정확한 자릿수 표는 두지 않았다(Claude 판단, 2026-11-04). 계좌 예금주 조회를 붙이면 그쪽이 확인한다.
 */
export function accountLooksOdd(value: string): boolean {
  const n = onlyDigits(value).length;
  return n < 10 || n > 16;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** 오늘 날짜(한국 시각, YYYY-MM-DD). 하루의 경계는 한국 시각이다(CODE_RULES 7절). */
export function todayKst(now: Date = new Date()): string {
  return new Date(now.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
